import { copyLocalCalendarDate } from './calendar-date.ts'
import { cloneStop, createFreshRoute } from './route-model.ts'
import type {
  PublishableQuickCreateValues,
  QuickCreateFormValues,
  QuickCreateLoadDefaults,
  QuickCreateValues,
} from './values.ts'

export const getQuickCreateInitialValues = ({
  currentUser,
  initialValues,
}: {
  currentUser: null | { id: number }
  initialValues?: QuickCreateLoadDefaults
}): QuickCreateFormValues => ({
  company_id: initialValues?.company_id ?? null,
  customer_rate_amount:
    initialValues?.customer_rate?.amount === undefined
      ? ''
      : String(initialValues.customer_rate.amount),
  customer_rate_currency: initialValues?.customer_rate?.currency ?? 'GBP',
  drops: (initialValues?.drops ?? createFreshRoute()).map(cloneStop),
  end_customer_company_id: initialValues?.end_customer_company_id ?? null,
  load_type_id: initialValues?.load_type_id ?? null,
  notes: initialValues?.notes ?? null,
  organisation_id: initialValues?.organisation_id ?? null,
  requires_end_customer: initialValues?.requires_end_customer ?? false,
  user_id: initialValues?.user_id ?? currentUser?.id ?? null,
  weight: initialValues?.weight,
  weight_unit: initialValues?.weight_unit ?? null,
})

export const buildQuickCreatePublishValues = (values: QuickCreateFormValues): QuickCreateValues => {
  const rate = values.customer_rate_amount.trim()
  const customerRate =
    rate === '' ? undefined : { amount: Number(rate), currency: values.customer_rate_currency }
  const notes = values.notes?.trim()
  const publishValues: QuickCreateValues = {
    company_id: values.company_id,
    drops: values.drops.map(stop => ({
      ...stop,
      date: copyLocalCalendarDate(stop.date),
      place: stop.place ? { ...stop.place } : null,
      reference_no: stop.reference_no?.trim() || null,
      time: { ...stop.time },
    })),
    end_customer_company_id: values.end_customer_company_id,
    load_type_id: values.load_type_id,
    organisation_id: values.organisation_id,
    requires_end_customer: values.requires_end_customer,
    user_id: values.user_id,
  }

  if (customerRate) publishValues.customer_rate = customerRate
  if (notes) publishValues.notes = notes
  if (values.weight !== undefined) publishValues.weight = values.weight
  if (values.weight_unit !== null) publishValues.weight_unit = values.weight_unit

  return publishValues
}

/** Narrows publish values to the shape `mapQuickCreateToPublishedLoad` requires. */
export const isPublishableQuickCreateValues = (
  values: QuickCreateValues,
): values is PublishableQuickCreateValues =>
  values.company_id != null &&
  values.load_type_id != null &&
  values.organisation_id != null &&
  values.user_id != null &&
  values.drops.every(drop => drop.place?.location_id != null)
